// ─── BILL RULES ───────────────────────────────────────────────────────────────
// The one place a saved bill id ("119-hr-1234", lowercase congress-type-number) becomes
// a display number or a link, and a saved policy area or tier becomes words.
//
// ⚠⚠ THE BILL IS SECTOR-LEVEL. The worker fans one bill out to every ticker in its policy
// area's list, so nothing here may read as "this bill names this company".

const BILL_PAGE_ROOT = "https://www.congress.gov/bill";

// ⚠⚠ `verified` IS true ONLY AFTER A PERSON HAS CLICK-CHECKED A REAL BILL OF THAT TYPE. All
// eight were, on 2026-11-05. A type added later starts false, and an unverified type shows its
// number as plain text, never a link.
export const BILL_TYPES = {
  // House bill. Example: H.R. 9495.
  hr: { label: "H.R.", path: "house-bill", verified: true },
  // Senate bill. Example: S. 1260.
  s: { label: "S.", path: "senate-bill", verified: true },
  // House joint resolution. Can become law, like a bill.
  hjres: { label: "H.J.Res.", path: "house-joint-resolution", verified: true },
  // Senate joint resolution. Can become law, like a bill.
  sjres: { label: "S.J.Res.", path: "senate-joint-resolution", verified: true },
  // House concurrent resolution. Both chambers, not sent to the President.
  hconres: { label: "H.Con.Res.", path: "house-concurrent-resolution", verified: true },
  // Senate concurrent resolution. Both chambers, not sent to the President.
  sconres: { label: "S.Con.Res.", path: "senate-concurrent-resolution", verified: true },
  // House simple resolution. House only.
  hres: { label: "H.Res.", path: "house-resolution", verified: true },
  // Senate simple resolution. Senate only.
  sres: { label: "S.Res.", path: "senate-resolution", verified: true },
};

// → { congress, type, number } or null. Strict, so a damaged id never reaches a link.
export function parseBillId(id) {
  const m = /^([1-9]\d{0,2})-([a-z]+)-([1-9]\d{0,5})$/.exec(typeof id === "string" ? id : "");
  return m ? { congress: Number(m[1]), type: m[2], number: m[3] } : null;
}

// 119 → "119th", 121 → "121st", 112 → "112th".
function ordinal(n) {
  const last2 = n % 100;
  if (last2 >= 11 && last2 <= 13) return `${n}th`;
  return `${n}${{ 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th"}`;
}

// "H.R. 1234", or null for a damaged id or a type not listed above.
export function billNumber(id, rules = BILL_TYPES) {
  const b = parseBillId(id);
  const rule = b && Object.hasOwn(rules, b.type) ? rules[b.type] : null;
  return rule ? `${rule.label} ${b.number}` : null;
}

// The page address the rules would give, verified or not. billUrl below decides whether
// it may be used.
export function billPagePath(id, rules = BILL_TYPES) {
  const b = parseBillId(id);
  const rule = b && Object.hasOwn(rules, b.type) ? rules[b.type] : null;
  return rule ? `${BILL_PAGE_ROOT}/${ordinal(b.congress)}-congress/${rule.path}/${b.number}` : null;
}

// The link for a bill, or null. Only a verified rule yields one.
export function billUrl(id, rules = BILL_TYPES) {
  const b = parseBillId(id);
  if (!b || !Object.hasOwn(rules, b.type) || rules[b.type].verified !== true) return null;
  return billPagePath(id, rules);
}

// The saved policy area → the badge. An area not listed reads as a plain sector bill.
const POLICY_BADGE = {
  "Armed Forces and National Security": "Defense bill",
  Health: "Health bill",
  Energy: "Energy bill",
  "Finance and Financial Sector": "Finance bill",
  "Foreign Trade and International Finance": "Trade bill",
  "Science, Technology, Communications": "Tech bill",
};
export const SECTOR_BILL = "Sector bill";
export function billBadge(policyArea) {
  return typeof policyArea === "string" && Object.hasOwn(POLICY_BADGE, policyArea)
    ? POLICY_BADGE[policyArea] : SECTOR_BILL;
}

export const BILL_BADGE_TITLE = "Sector-level: a bill in the same policy area as this company's sector. Select for details.";
export const BILL_NOTE = "Sector-level: this bill is in the same policy area as this company's sector. It doesn't say the bill names this company.";

// The saved tier → the stage. ⚠ Tier 5 runs from one chamber passing it to becoming law,
// so it says only that. Tier 0 or anything else is no stage at all.
const STAGE_WORDS = {
  1: "introduced",
  2: "referred to committee",
  3: "in committee",
  4: "reported by committee",
  5: "passed a chamber or later",
};
export function billStage(tier) {
  return Number.isInteger(tier) && Object.hasOwn(STAGE_WORDS, tier) ? STAGE_WORDS[tier] : null;
}

// The saved signals_fired labels → plain words. A label not listed is not shown.
const SIGNAL_WORDS = {
  "dark_pool_buyer+unusual_calls": "Dark pool buying with unusual calls",
  "dark_pool_seller+unusual_puts": "Dark pool selling with unusual puts",
  dark_pool_buyer: "Dark pool buying",
  dark_pool_seller: "Dark pool selling",
  dark_pool_neutral: "Dark pool activity",
  dark_pool_active: "Dark pool activity",
  unusual_calls: "Unusual calls",
  unusual_puts: "Unusual puts",
  congressional: "Reported congressional trade",
  fda_catalyst: "FDA catalyst",
  midpoint_cluster: "Midpoint print cluster",
  legislation: "Sector bill",
  autocall_at_barrier: "Structured-note barrier reached",
  autocall_below_barrier: "Structured-note barrier breached",
};
export function signalWord(label) {
  const days = /^earnings_(\d+)d$/.exec(typeof label === "string" ? label : "");
  if (days) {
    const n = Number(days[1]);
    return n === 0 ? "Earnings today" : `Earnings in ${n} day${n === 1 ? "" : "s"}`;
  }
  return typeof label === "string" && Object.hasOwn(SIGNAL_WORDS, label) ? SIGNAL_WORDS[label] : null;
}

// The saved score_parts keys → plain words. Rows written before these existed show none.
const PART_WORDS = {
  tier: "Points for its stage",
  sponsor_traded_sector: "Sponsor traded this sector",
  committee_alignment: "Sponsor sits on the committee",
  darkpool_buyer: "Dark pool buying across the sector",
  unusual_options: "Unusual options across the sector",
  sponsor_ticker_flow: "Activity in the sponsor's traded names",
};
export function scoreParts(parts) {
  if (!parts || typeof parts !== "object" || Array.isArray(parts)) return [];
  return Object.entries(parts)
    .filter(([k, v]) => Object.hasOwn(PART_WORDS, k) && Number.isFinite(v))
    .map(([k, v]) => ({ key: k, word: PART_WORDS[k], points: v }));
}
